import { Injectable } from "@angular/core";
import { CookieService } from "ngx-cookie-service";
import { COOKIE_STORAGE } from "../constants";
import { ThemeService } from "./themes.service";
import { BehaviorSubject, Observable } from "rxjs";
import { Router } from "@angular/router";
import { ApiService } from "./api.service";
import { environment } from "src/environments/environment";
import { CookieSettingsComponent } from "../cookie-settings/cookie-settings.component";
import { MatDialog } from "@angular/material/dialog";

@Injectable({
    providedIn: 'root'
})

export class CookieConsentService {

    selectedRegion: any;
    setHomepage = environment.HOMEPAGE;

    cookieConsentValuesSubject = new BehaviorSubject<any>({});
    cookieConsentValues$: Observable<any> = this.cookieConsentValuesSubject.asObservable();

    isAllCookiesAllowedSubject = new BehaviorSubject<any>(false);
    allCookiesAllowed$: Observable<any> = this.isAllCookiesAllowedSubject.asObservable();

    focusAllowCookiesButtonSubject = new BehaviorSubject<any>(false);
    focusAllowCookiesButton$: Observable<any> = this.focusAllowCookiesButtonSubject.asObservable();

    showCookieConsentSubject = new BehaviorSubject<any>(false);
    showCookieConsent$: Observable<any> = this.showCookieConsentSubject.asObservable();

    isCookiesConsentHiddenSubject = new BehaviorSubject<any>(false);
    cookiesConsentHidden$: Observable<any> = this.isCookiesConsentHiddenSubject.asObservable();

    cookieConsentErrorSubject = new BehaviorSubject<any>({});
    cookieConsentError$: Observable<any> = this.cookieConsentErrorSubject.asObservable();

    constructor(
        private router: Router,
        private dialog: MatDialog,
        private themeService: ThemeService,
        private cookieService: CookieService,
        private apiService: ApiService,
    ) {
        this.themeService.selectedRegion$.subscribe((response: any) => {
            this.selectedRegion = response;
        });

        this.isAllConsentApproved();
    }

    setCookieConsetInCookieService(payload: any) {
        let formattedString = Object.entries(payload)
            .map(([key, value]) => {
                let formattedKey = key;
                let formattedValue: any = value === true ? "true" : value === false ? "false" : `'${value}'`;
                return `'${formattedKey}': ${formattedValue}`;
            }).join('|');


        formattedString = '"' + formattedString + '"';

        this.setCookie(`${COOKIE_STORAGE.COOKIE_CONSENT}-${this.selectedRegion}`, formattedString, 365);
    }

    setCookie(cookieName: string, cookieValue: string, days: number): void {
        const expiryDate = new Date();
        expiryDate.setDate(expiryDate.getDate() + days);
        document.cookie = `${cookieName}=${cookieValue}; expires=${expiryDate.toUTCString()}; path=/`;
    }

    sanitizeCookieValue(value: string): string {
        try {
            let jsonString = value.replace(/\|/g, ', ');
            jsonString = jsonString.replace(/'/g, '"');
            jsonString = jsonString.replace(/\bTrue\b/g, 'true');
            if (jsonString.startsWith('""')) {
                jsonString = jsonString.slice(1, -1);
            }

            const a = "{" + jsonString + "}";
            let jsonObject = JSON.parse(a);

            return jsonObject;
        } catch (error) {
            this.showCookieConsentSubject.next(true);
            this.isCookiesConsentHiddenSubject.next(false);
            this.isAllCookiesAllowedSubject.next(false);
            this.router.navigate(['/', this.setHomepage]);

            this.cookieConsentErrorSubject.next({
                type: 'warning',
                message: "please allow necessary cookies to continue exploring",
            });

            this.openCookieSettingsComponent(true, false, false, false, false);
            throw error;
        }
    }

    isAllConsentApproved() {
        const isConsentGiven = this.cookieService.get(`${COOKIE_STORAGE.COOKIE_CONSENT}-${this.selectedRegion}`) ? true : false;
        if (isConsentGiven) {
            let givenConsent: any;
            if ('LOCAL_COOKIE' in environment && (environment as any).LOCAL_COOKIE) {
                const storedConsent = this.cookieService.get(`${COOKIE_STORAGE.COOKIE_CONSENT}-${this.selectedRegion}`);
                if (storedConsent) {
                    givenConsent = this.sanitizeCookieValue(storedConsent);
                    this.cookieConsentValuesSubject.next(givenConsent);
                }
            } else {
                const storedConsent = this.cookieService.get(`${COOKIE_STORAGE.COOKIE_CONSENT}-${this.selectedRegion}`);
                if (storedConsent) {
                    givenConsent = this.sanitizeCookieValue(storedConsent);
                    this.cookieConsentValuesSubject.next(givenConsent);
                }
            }

            if (givenConsent?.necessary_cookies_consent && givenConsent?.performance_cookies_consent
                && givenConsent?.marketing_cookies_consent && givenConsent?.analytical_cookies_consent
                && givenConsent?.functional_cookies_consent) {
                this.isAllCookiesAllowedSubject.next(true);
                return true;
            }
        }

        return false;
    }

    openCookieSettingsComponent(
        isNecessaryCookiesAllowed: boolean,
        isPerformanceCookiesAllowed: boolean,
        isMarketingCookiesAllowed: boolean,
        isAnalyticalCookiesAllowed: boolean,
        isFunctionalCookiesAllowed: boolean,
    ) {
        this.showCookieConsentSubject.next(false);
        this.isCookiesConsentHiddenSubject.next(true);

        const isDialogOpen = this.dialog.openDialogs.some(dialog => dialog.componentInstance instanceof CookieSettingsComponent);
        if (isDialogOpen) {
            return;
        }

        const dialogRef = this.dialog.open(CookieSettingsComponent, {
            maxWidth: "80%",
            data: {
                isNecessaryCookiesAllowed: (isNecessaryCookiesAllowed || true),
                isPerformanceCookiesAllowed: isPerformanceCookiesAllowed,
                isMarketingCookiesAllowed: isMarketingCookiesAllowed,
                isAnalyticalCookiesAllowed: isAnalyticalCookiesAllowed,
                isFunctionalCookiesAllowed: isFunctionalCookiesAllowed,
            }
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (!result) {
                this.showCookieConsentSubject.next(true);
                this.isCookiesConsentHiddenSubject.next(false);
                return;
            } else if (result === 'allowAll') {
                this.allowAllCookies();
            } else if (result === 'denyAll') {
                this.hideCookieConsent();
            } else if (result?.result === 'allowSelected') {
                this.giveConsent(result);
            } else {
                this.showCookieConsentSubject.next(true);
                this.isCookiesConsentHiddenSubject.next(false);
                this.cookieConsentValuesSubject.next({
                    isNecessaryCookiesAllowed: result?.necessaryCookies,
                    isPerformanceCookiesAllowed: result?.performanceCookies,
                    isMarketingCookiesAllowed: result?.marketingCookies,
                    isAnalyticalCookiesAllowed: result?.analyticalCookies,
                    isFunctionalCookiesAllowed: result?.functionalCookies,
                });
            }
        });
    }

    async allowAllCookies() {
        const cookieConsentPayload = {
            consent: true,
            ip: this.apiService.ipAddress || COOKIE_STORAGE.LOCAL_IP_ADDRESS,
            country_code: this.selectedRegion,
            necessary_cookies_consent: true,
            performance_cookies_consent: true,
            marketing_cookies_consent: true,
            analytical_cookies_consent: true,
            functional_cookies_consent: true,
        };

        this.setCookieConsetInCookieService(cookieConsentPayload);
        this.isAllCookiesAllowedSubject.next(true);
        this.showCookieConsentSubject.next(false);
        this.cookieConsentErrorSubject.next(null);

        this.apiService.sendCookieConsentOfCustomer(cookieConsentPayload).subscribe((response) => {
            // Got the response
            console.warn(response);
        });
    }

    async giveConsent(cookieConsentValues: any) {
        const cookieConsentPayload = {
            consent: true,
            ip: this.apiService.ipAddress || COOKIE_STORAGE.LOCAL_IP_ADDRESS,
            country_code: this.selectedRegion,
            necessary_cookies_consent: cookieConsentValues?.necessaryCookies,
            performance_cookies_consent: cookieConsentValues?.performanceCookies,
            marketing_cookies_consent: cookieConsentValues?.marketingCookies,
            analytical_cookies_consent: cookieConsentValues?.analyticalCookies,
            functional_cookies_consent: cookieConsentValues?.functionalCookies,
        };

        this.setCookieConsetInCookieService(cookieConsentPayload);

        this.showCookieConsentSubject.next(false);
        this.cookieConsentErrorSubject.next(null);
        if (cookieConsentPayload?.necessary_cookies_consent && cookieConsentPayload?.performance_cookies_consent
            && cookieConsentPayload?.marketing_cookies_consent && cookieConsentPayload?.analytical_cookies_consent
            && cookieConsentPayload?.functional_cookies_consent) {
            this.isAllCookiesAllowedSubject.next(true);
        }

        const requestPayload = {
            isConsent: true,
            ...cookieConsentPayload,
        }

        this.apiService.sendCookieConsentOfCustomer(requestPayload).subscribe((response) => {
            if (cookieConsentPayload?.necessary_cookies_consent && cookieConsentPayload?.performance_cookies_consent
                && cookieConsentPayload?.marketing_cookies_consent && cookieConsentPayload?.analytical_cookies_consent
                && cookieConsentPayload?.functional_cookies_consent) {
                this.isAllCookiesAllowedSubject.next(true);
            }
        }, (error) => {
            console.error(error);
        });
    }


    hideCookieConsent() {
        this.cookieService.delete(`${COOKIE_STORAGE.COOKIE_CONSENT}-${this.selectedRegion}`, "/", location.hostname);
        this.isAllCookiesAllowedSubject.next(false);
        this.showCookieConsentSubject.next(false);
        this.cookieConsentErrorSubject.next(null);
        this.isCookiesConsentHiddenSubject.next(true);
        this.router.navigate(['/', this.setHomepage]);
    }

    showCookieConsentWithError(message?: any) {
        this.showCookieConsentSubject.next(true);
        this.focusAllowCookiesButtonSubject.next(true);
        if (message?.trim()) {
            this.cookieConsentErrorSubject.next({
                type: 'error',
                message,
            });
        }
    }

    deleteAllowedCookies() {
        // this.cookieService.delete(`${COOKIE_STORAGE.COOKIE_CONSENT}-${this.selectedRegion}`, "/", location.hostname);
        this.isAllCookiesAllowedSubject.next(false);
        this.router.navigate(['/', this.setHomepage]);
    }

    async checkCookieConsent(ipAddress?: any) {
        let ipAddressResponse: any;
        if (environment.GET_IP_ADDRESS) {
            if (!ipAddress) {
                ipAddressResponse = await this.apiService.getIpAddress();
                if (ipAddressResponse) {
                    this.apiService.ipAddress = ipAddressResponse.origin;
                    this.apiService.systemInformation.ipAddress = this.apiService.ipAddress;
                }
            }
        }

        this.themeService.selectedRegion$.subscribe((response) => {
            this.selectedRegion = response;
        });

        let givenConsent: any;
        const storedConsent = this.cookieService.get(`${COOKIE_STORAGE.COOKIE_CONSENT}-${this.selectedRegion}`);
        if (storedConsent) {
            givenConsent = this.sanitizeCookieValue(storedConsent);
        }

        if (givenConsent && givenConsent?.consent) {
            if ((this.apiService.ipAddress || COOKIE_STORAGE.LOCAL_IP_ADDRESS) === givenConsent?.ip) {
                return true;
            } else {
                this.cookieConsentErrorSubject.next({
                    type: 'warning',
                    message: "please allow necessary cookies to continue exploring",
                });

                this.router.navigate(['/', this.setHomepage]);
                this.isCookiesConsentHiddenSubject.next(false);
                this.focusAllowCookiesButtonSubject.next(true);
                return false;
            }
        } else {
            this.showCookieConsentSubject.next(true);
            this.isCookiesConsentHiddenSubject.next(false);
            this.isAllCookiesAllowedSubject.next(false);
            this.router.navigate(['/', this.setHomepage]);

            this.cookieConsentErrorSubject.next({
                type: 'warning',
                message: "please allow necessary cookies to continue exploring",
            });

            this.openCookieSettingsComponent(
                givenConsent?.necessary_cookies_consent,
                givenConsent?.performance_cookies_consent,
                givenConsent?.marketing_cookies_consent,
                givenConsent?.analytical_cookies_consent,
                givenConsent?.functional_cookies_consent,
            );

            return false;
        }
    }
}